import { motion } from 'framer-motion';
import type { ReactNode } from 'react';
import { useTranslation } from 'react-i18next';

import { Box, ChatAlt2Outline, DocumentTextOutline, Flex, Link, SparklesOutline, Text } from '@bonusly/design-system';

export type ContentCardType = 'recognition' | 'meeting' | 'generic';

export interface ContentCardMetadataItem {
	key: string;
	value: string;
}

export interface BizyContentCardProps {
	type?: ContentCardType;
	title?: string;
	content: string;
	href?: string;
	metadata?: ContentCardMetadataItem[];
	icon?: ReactNode;
}

const TYPE_CONFIG: Record<ContentCardType, { labelKey: string; icon: ReactNode; color: string }> = {
	recognition: { labelKey: 'content_card.label.recognition', icon: <SparklesOutline boxSize={3.5} />, color: 'primary.fg' },
	meeting: { labelKey: 'content_card.label.meeting', icon: <ChatAlt2Outline boxSize={3.5} />, color: 'info.icon' },
	generic: { labelKey: 'content_card.label.generic', icon: <DocumentTextOutline boxSize={3.5} />, color: 'text.softer' },
};

export function BizyContentCard({ type = 'generic', title, content, href, metadata, icon }: BizyContentCardProps) {
	const { t } = useTranslation('admin_bizy');
	const config = TYPE_CONFIG[type];
	const displayIcon = icon ?? config.icon;
	const label = t(config.labelKey);

	return (
		<motion.div initial={{ opacity: 0, y: 4 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.15, ease: 'easeOut' }}>
			<Box border="1px solid" borderColor="border.soft" borderRadius={12} bg="bg.surface" overflow="hidden" px={3} py={2.5}>
				<Flex direction="column" gap={2}>
					<Flex align="center" gap={1.5}>
						<Flex color={config.color} flexShrink={0}>
							{displayIcon}
						</Flex>
						<Text fontSize="xs" fontWeight="medium" color={config.color} textTransform="uppercase" letterSpacing="wider">
							{label}
						</Text>
					</Flex>

					{title && (
						<Text fontSize="sm" fontWeight="semibold" color="text.default">
							{title}
						</Text>
					)}

					<Text fontSize="sm" color="text.soft" lineHeight="tall" noOfLines={4}>
						{content}
					</Text>

					{metadata && metadata.length > 0 && (
						<Flex gap={3} flexWrap="wrap">
							{metadata.map((item) => (
								<Flex key={item.key} gap={1} align="baseline">
									<Text fontSize="xs" color="text.softer">
										{item.key}
									</Text>
									<Text fontSize="xs" color="text.default" fontWeight="medium">
										{item.value}
									</Text>
								</Flex>
							))}
						</Flex>
					)}

					{href && (
						<Link href={href} color="text.interactive" fontSize="xs" target="_blank" rel="noopener noreferrer">
							{t('approval_card.view')}
						</Link>
					)}
				</Flex>
			</Box>
		</motion.div>
	);
}
